import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, TrendingUp, Users } from "lucide-react";
import { scrollToId, whatsappLink } from "@/site-data";

const STATS = [
  { value: "120+", label: "YÖNETİLEN KAMPANYA" },
  { value: "1.2M+", label: "AYLIK ERİŞİM" },
  { value: "%38", label: "ORT. DÖNÜŞÜM ARTIŞI" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yGhost = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28">
      <motion.span
        style={{ y: yGhost }}
        aria-hidden
        className="text-outline-faint pointer-events-none absolute -top-8 left-0 select-none font-syne text-[26vw] font-extrabold leading-none tracking-tighter"
      >
        ECN
      </motion.span>
      <div className="pointer-events-none absolute -right-40 top-20 h-[480px] w-[480px] rounded-full bg-[#1E7FFF]/20 blur-[140px]" />
      <div className="pointer-events-none absolute -left-40 bottom-0 h-[380px] w-[380px] rounded-full bg-[#33C2FF]/10 blur-[140px]" />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 md:px-8 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="font-code text-xs tracking-[0.25em] uppercase text-[#5CA3FF]"
          >
            ECN Dijital — Stratejiyle Büyürsün
          </motion.p>

          <motion.h1
            data-testid="hero-headline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 max-w-xl text-4xl font-bold leading-[1.15] tracking-tight sm:text-5xl lg:text-6xl"
          >
            Markanızı Dijitalde <span className="text-[#1E7FFF]">Büyütüyoruz.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 max-w-xl text-base leading-relaxed text-[#B9C4E8] md:text-lg"
          >
            Sosyal medya yönetiminden Meta ve Google reklamlarına, içerik üretiminden
            stratejik planlamaya — dijital büyümeniz için ihtiyacınız olan her şey tek çatı altında.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-form"
              onClick={() => scrollToId("#iletisim")}
              className="group flex items-center gap-2 rounded-full bg-[#1E7FFF] px-7 py-3.5 text-sm font-semibold text-white shadow-[0_0_30px_rgba(30,127,255,0.4)] transition-all duration-300 hover:bg-[#5CA3FF] hover:shadow-[0_0_40px_rgba(92,163,255,0.5)]"
            >
              Ücretsiz Dijital Analiz Talep Et
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="flex items-center gap-2 rounded-full border border-[#2E4390] px-7 py-3.5 text-sm font-semibold text-[#F8FAFC] transition-colors duration-300 hover:border-[#25D366] hover:text-[#25D366]"
            >
              <MessageCircle size={16} />
              WhatsApp'tan Yaz
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-12 flex flex-wrap gap-8 border-t border-[#2E4390]/60 pt-6"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <p className="font-syne text-2xl font-bold text-[#5CA3FF]">{s.value}</p>
                <p className="font-code mt-1 text-[10px] tracking-[0.2em] text-[#8B9AC4]">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ y: yVisual }} className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-3xl border border-[#2E4390] bg-[#122263]/70 p-6 shadow-[0_0_60px_rgba(30,127,255,0.25)] backdrop-blur-md"
            >
              <div className="rounded-2xl bg-white p-8">
                <img src="/assets/ecn-logo.png" alt="ECN Dijital" className="w-full object-contain" />
              </div>
              <p className="font-code mt-5 text-center text-[10px] tracking-[0.3em] uppercase text-[#8B9AC4]">
                Dijital Pazarlama & Sosyal Medya Uzmanlığı
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
              className="animate-floaty absolute -left-6 top-8 flex items-center gap-3 rounded-2xl border border-[#2E4390] bg-[#1A2E78]/90 px-4 py-3 backdrop-blur-md"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1E7FFF]/20 text-[#5CA3FF]">
                <TrendingUp size={16} />
              </span>
              <div>
                <p className="font-syne text-sm font-bold text-[#F8FAFC]">+%212 ROAS</p>
                <p className="font-code text-[9px] tracking-[0.15em] text-[#8B9AC4]">META ADS</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              style={{ animationDelay: "1.4s" }}
              className="animate-floaty absolute -right-4 bottom-10 flex items-center gap-3 rounded-2xl border border-[#2E4390] bg-[#1A2E78]/90 px-4 py-3 backdrop-blur-md"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#33C2FF]/15 text-[#33C2FF]">
                <Users size={16} />
              </span>
              <div>
                <p className="font-syne text-sm font-bold text-[#F8FAFC]">1.4M Erişim</p>
                <p className="font-code text-[9px] tracking-[0.15em] text-[#8B9AC4]">AYLIK ORTALAMA</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
